import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { DATE_TIME_FORMAT_SHORTER } from '../../config';
import { PortalUser } from '../../server-api/model';
import { GenericSorter } from '../../state/genericSorter';
import { userRolesReversed, usersContext } from '../../state/usersState';
import { useHorizontalDragScroll, useIsSmallViewport } from '../../utils/hooks';
import { Spinner } from '../common/spinner/Spinner';
import { TableRowWrapper } from '../common/table/TableRow';
import { SortHeaderCell } from '../common/table/SortHeaderCell';
import { UserInfo } from './UserInfo';
import './UserList.css';

export const UserList = observer(() => {
  return (
    <div className='UserList'>
      <UserTable />
    </div>
  );
});

const UserTable = observer(() => {
  const ref = useRef<HTMLTableElement>(null);
  const usersState = useContext(usersContext);
  useHorizontalDragScroll(ref);

  const selectionPool = usersState.selectedUsers;
  const users = usersState.users;
  const isSmallViewport = useIsSmallViewport();

  const [sorter, setSorter] = useState(
    new GenericSorter(users, 'usersName', true)
  );

  useEffect(() => {
    setSorter(new GenericSorter(users, sorter.sortBy, sorter.sortAscending));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users]);

  const cellsHeaders: Record<string, string> = {
    fullName: 'Name',
    userName: 'Username',
    jobTitle: 'Job title',
    permissions: 'Permissions',
    admin: 'Administrator',
    lastLogin: 'Last login',
    status: 'Status'
  };

  const jsxEntries = useMemo(() => {
    return sorter.sortedEntries.map((user: PortalUser, index) => {
      const cellsToShow = {
        fullName: user.fullName,
        userName: user.login,
        jobTitle: user.position,
        permissions: userRolesReversed[user.role],
        admin: user.admin ? 'Yes' : 'No',
        lastLogin: user.lastLogin
          ? moment(user.lastLogin).format(DATE_TIME_FORMAT_SHORTER)
          : 'Never',
        status: user.active ? 'Active' : 'Inactive',
      };
      return (
        <TableRowWrapper
          key={'row' + index}
          item={user}
          cellsToShow={cellsToShow}
          cellsHeaders={cellsHeaders}
          expansion={<UserInfo user={user} />}
          selectionPool={selectionPool}
          compactViewMode={isSmallViewport}
          colSpan={9}
        />
      );
    })
  }, [sorter.sortedEntries, isSmallViewport]);

  return (
    <div className='StandardTableWrapper' aria-labelledby='manageUsersLabel'>
      {isSmallViewport ? (<div className='StandardTable'>{jsxEntries}</div>) :
        (<table
          role='table'
          className='UserTable StandardTable expandable'
          ref={ref}
          aria-labelledby='manageUsersLabel'
        >
          <thead>
            <tr className='header'>
              <th className='cell-chevron'>
                <span className='hidden-header'>Expand</span>
              </th>

              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='usersName'
                text={cellsHeaders.fullName}
                className='cell-fullname'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='usersUsername'
                text={cellsHeaders.userName}
                className='cell-username'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter} type='usersJob'
                text={cellsHeaders.jobTitle}
                className='cell-jobtitle'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='usersPermissions'
                text={cellsHeaders.permissions}
                className='cell-permissions'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='usersAdmin'
                text={cellsHeaders.admin}
                className='cell-admin'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='usersLastlogin'
                text={cellsHeaders.lastLogin}
                className='cell-lastlogin'
              />
              <SortHeaderCell
                mode='td'
                sorter={sorter}
                type='usersStatus'
                text={cellsHeaders.status}
                className='cell-status'
              />

              <th className='cell-checkbox'>
                <span className='hidden-header'>Select</span>
              </th>
            </tr>
          </thead>

          <tbody>
            {jsxEntries}
          </tbody>
        </table>)}

      {usersState.gettingUsers ? <Spinner blocking={true} /> : null}
    </div>
  );
});
